import {
  autoUpdate,
  FloatingFocusManager,
  FloatingOverlay,
  FloatingPortal,
  flip,
  offset,
  shift,
  useFloating,
  useInteractions,
  useRole,
} from '@floating-ui/react'
import { type CSSProperties, css, cx } from '@linaria/core'
import { type FC, type ReactNode, useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { IconButton } from '#components/IconButton'
import CloseIcon from '#icons/close-48.svg'
import { fragments, style } from '#styles'

const toastClass = css`
  position: fixed;

  animation: 0.5s reveal ease;

  &[data-placement^="top"] {
    --reveal-translation: -150%;
  }

  &[data-placement^="bottom"] {
    --reveal-translation: 150%;
  }

  @keyframes reveal {
    0% {
      transform: translate(-50%, var(--reveal-translation));
    }

    100% {
      transform: translate(-50%, 0%);
    }
  }

  &[data-placement="top"] {
    left: 50%;
    top: 12px;
    transform: translate(-50%, 0);
  }

  &[data-placement="top-left"] {
    top: 12px;
    left: 12px;
  }

  &[data-placement="top-right"] {
    top: 12px;
    right: 12px;
  }

  &[data-placement="bottom"] {
    left: 50%;
    bottom: 12px;
    transform: translate(-50%, 0);
  }

  &[data-placement="bottom-left"] {
    bottom: 12px;
    left: 12px;
  }

  &[data-placement="bottom-right"] {
    bottom: 12px;
    right: 12px;
  }
`

const toastElementClass = css`
  isolation: isolate;
  width: 100%;
  max-width: 600px;

  &[data-appearance='primary'] {
    --background-color: ${style.theme.backgroundSurfaceStandardDefault};
    --background-hover-color: ${style.theme.backgroundSurfaceStandardStrong};
    --text-color: ${style.theme.textInverse};
    --timer-line-color: ${style.theme.textSoft};
  }

  &[data-appearance='secondary'] {
    --background-color: ${style.theme.backgroundSurfaceTertiary};
    --background-hover-color: ${style.theme.backgroundSurfaceTertiaryStrong};
    --text-color: ${style.theme.textSoft};
    --timer-line-color: ${style.theme.textSoft};
  }

  &[data-appearance='warning'] {
    --background-color: ${style.theme.backgroundSemanticWarning};
    --background-hover-color: ${style.theme.backgroundSemanticWarning};
    --text-color: ${style.theme.textWarning};
    --timer-line-color: ${style.theme.borderWarning};
  }

  &[data-appearance='error'],
  &[data-appearance='important'] {
    --background-color: ${style.theme.backgroundSemanticDanger};
    --background-hover-color: ${style.theme.backgroundSemanticDanger};
    --text-color: ${style.theme.textDanger};
    --timer-line-color: ${style.theme.borderDanger};
  }

  > ._content {
    display: flex;
    flex-direction: column;
    background-color: ${style.theme.backgroundSurfacePrimaryStrong};
    background-color: var(--background-color);
    color: var(--text-color);
    border-radius: 0 !important;
    padding: 8px 12px;
    transition: 0.25s background-color ease;

    > ._title-row {
      display: flex;
      flex-direction: row;
      justify-content: space-between;

      > ._title {
        ${fragments.textStyle.body.s.condensedBold}
      }

      > ._gap {
        flex: 1;
        min-width: 16px;
      }
      
      ._close {
        cursor: pointer;
        transition: all 250ms;
        display: flex;
        flex-direction: column;
        justify-content: flex-start;
      }
    }
    
    > p._message {
      white-space: preserve;
    }
  }

  > ._timing-indicator {
    position: relative;
    height: 4px;
    background-color: var(--background-hover-color);
    overflow: hidden;

    &::after {
      content: ' ';
      height: 4px;
      display: block;
      background-color: var(--timer-line-color);
      width: 100%;
      animation: var(--dismiss) timeout linear;
      animation-fill-mode: forwards;
    }

    @keyframes timeout {
      0% {
        width: 100%;
      }

      12.5% {
        width: 100%;
      }

      100% {
        width: 0%;
      }
    }
  }
`

export interface ToastProperties {
  onDismiss?: () => void
  visibleInMs?: number
  title?: string
  message?: string
  children?: ReactNode
  onClick?: () => void
  appearance?: 'primary' | 'secondary' | 'important' | 'warning' | 'error'
  'data-testid'?: string
}

/** the look of a toast element */
export const ToastElement: FC<ToastProperties> = ({
  onDismiss,
  visibleInMs,
  title,
  message,
  children,
  onClick,
  appearance = 'primary',
  'data-testid': dataTestId,
}) => {
  const { t } = useTranslation('shared')
  const onClickThrough = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      onClick ? onClick() : onDismiss?.()
    },
    [onClick, onDismiss],
  )
  const hasTimeout = visibleInMs !== undefined && visibleInMs > 0
  return (
    <div className={toastElementClass} onClick={onClickThrough} data-appearance={appearance} data-testid={dataTestId}>
      <div className={cx('_content', hasTimeout && '_with-timeout')}>
        {(title ?? onDismiss) && (
          <div className="_title-row">
            {title && <div className="_title">{title}</div>}
            <div className="_gap" />
            {onDismiss && (
              <IconButton
                className="_close"
                iconUrl={CloseIcon}
                onClick={(e) => {
                  e.stopPropagation()
                  onDismiss?.()
                }}
                title={t('common.button.close', 'Close')}
              />
            )}
          </div>
        )}
        <p className="_message">{message}</p>
        {children}
      </div>
      {hasTimeout && <div className="_timing-indicator" style={{ '--dismiss': `${visibleInMs - 550}ms` } as CSSProperties} />}
    </div>
  )
}

/** an individual toast to be placed anywhere */
export const Toast: FC<{
  placement: 'top' | 'top-left' | 'top-right' | 'bottom' | 'bottom-left' | 'bottom-right'
  onDismiss?: () => unknown
  visibleInMs?: number
  title?: string
  children?: ReactNode
  onClick?: () => unknown
  appearance?: 'primary' | 'secondary' | 'important' | 'warning' | 'error'
}> = ({ placement, visibleInMs, ...rest }) => {
  const [open, setOpen] = useState(true)
  const [openTime] = useState(Date.now())

  useEffect(() => {
    if (!visibleInMs) {
      return
    }
    const time = Date.now()
    const closeTime = openTime + visibleInMs
    const timeLeft = closeTime - time

    const handle = setTimeout(() => {
      setOpen(false)
    }, timeLeft)
    return () => {
      clearTimeout(handle)
    }
  }, [visibleInMs, openTime])

  const { refs, context } = useFloating({
    open: open,
    onOpenChange: setOpen,
    middleware: [offset(10), flip(), shift()],
    whileElementsMounted: autoUpdate,
  })

  const role = useRole(context, {
    role: 'label',
  })

  const { getFloatingProps } = useInteractions([role])

  return (
    open && (
      <FloatingPortal>
        <FloatingOverlay>
          <FloatingFocusManager context={context}>
            <div ref={refs.setFloating} {...getFloatingProps()} className={toastClass} data-placement={placement}>
              <ToastElement visibleInMs={visibleInMs} {...rest} />
            </div>
          </FloatingFocusManager>
        </FloatingOverlay>
      </FloatingPortal>
    )
  )
}
